import JQuery from "jquery";
import { TChallengeTemplate } from "../../Types/TChallengeTemplate";
import TemplateLazyLoad from "../../../components/Catalog/TemplateLazyLoad";
import { EChallengeType } from "../../Types/EChallengeType";

/**
 * Fills a ChallengeTemplateBody.twig skeleton from a challenge template:
 * banner carousel, share link, title, duration, concepts and description
 * blocks. Scoped to the skeleton's root element, so several instances can
 * coexist on one page (catalog popup, pathway inline cards).
 */
export default class ChallengeTemplateBodyRenderer {
  private readonly root: JQuery<HTMLElement>;
  private readonly carouselContainer: JQuery<HTMLElement>;
  private readonly conceptTemplate: JQuery<HTMLElement>;

  private template?: TChallengeTemplate;
  private imageUrl?: string;
  private carouselIndex = 0;
  private carouselImages: Array<string> = [];
  private carouselLoaders: Array<TemplateLazyLoad> = [];

  constructor(root: JQuery<HTMLElement>) {
    this.root = root;
    this.carouselContainer = root.find(".carousel-container");
    this.conceptTemplate = root.find(".concept-item-template");
  }

  public Render(template: TChallengeTemplate, imageUrl: string): void {
    this.template = template;
    this.imageUrl = imageUrl;

    this.root.toggleClass(
      "--reading-template",
      template.type === EChallengeType.READING,
    );
    this.root.toggleClass(
      "--writing-template",
      template.type === EChallengeType.WRITING,
    );

    this.renderCarousel();
    this.renderText();
    this.renderConcepts();
  }

  private renderText(): void {
    const name = this.root.find(".js-title");
    const studentDescription = this.root.find(".student-description");
    const teacherDescription = this.root.find(".teacher-description");
    const noteForTeacher = this.root.find(".note-for-teacher");
    const educationalIntent = this.root.find(".educational-intent");
    const expectedDuration = this.root.find(".expected-duration");

    // Share/copy link to challenge
    if (this.template.id !== null) {
      const clipboardUrl =
        this.root.find(".template-link").attr("data-base-url") +
        "/" +
        this.template.id;
      this.root
        .find(".template-link")
        .attr("data-clipboard-text", clipboardUrl);
      this.root.find(".data-code-url").html(clipboardUrl);
    }

    if (this.template.name !== null) {
      name.text(this.template.name);
    }

    if (this.template.studentInstructions !== null) {
      studentDescription
        .find(".text-block")
        .html(this.template.studentInstructions);
      studentDescription.removeClass("hide");
    } else {
      studentDescription.addClass("hide");
    }

    if (this.template.educationalIntent !== null) {
      educationalIntent
        .find(".text-block")
        .html(this.template.educationalIntent);
      educationalIntent.removeClass("hide");
    } else {
      educationalIntent.addClass("hide");
    }

    if (this.template.expectedDuration !== null) {
      expectedDuration.find(".text-block").text(this.template.expectedDuration);
      expectedDuration
        .find(".text-block")
        .attr("title", this.template.expectedDuration);
      expectedDuration.removeClass("hide");
    } else {
      expectedDuration.addClass("hide");
    }

    if (this.template.description) {
      teacherDescription.find(".text-block").html(this.template.description);
      teacherDescription.removeClass("hide");
    } else {
      teacherDescription.addClass("hide");
    }

    if (this.template.noteForTeacher) {
      noteForTeacher.find(".text-block").html(this.template.noteForTeacher);
      noteForTeacher.removeClass("hide");
    } else {
      noteForTeacher.addClass("hide");
    }
  }

  private renderConcepts(): void {
    const conceptContainer = this.root.find(".concepts-list");
    const lang = $("html").attr("lang");
    conceptContainer.html("");
    for (const concept of this.template.concepts) {
      const clone = this.conceptTemplate.clone();
      clone.html(concept.translations[lang] as string);
      clone.attr("style", `--item-color: ${concept.color}`);
      conceptContainer.append(clone);
      clone.removeClass("hide concept-item-template");
      clone.addClass("concept-item");
    }
  }

  private renderCarousel(): void {
    const templateContainer = this.carouselContainer.find(
      ".image-row-template",
    );
    const carousel = this.carouselContainer.find(".carousel");

    const { images } = this.template;

    this.carouselLoaders.forEach((loader) => {
      loader.Destroy();
    });
    this.carouselLoaders = [];
    // Clears carousel
    carousel.html("");

    this.carouselImages = images;
    // Clones images
    for (const imageUrl of images) {
      if (imageUrl !== undefined && imageUrl !== "") {
        const clone = templateContainer.clone();
        clone.find(".lazy-template-image").attr("data-src", `${this.imageUrl}`);
        this.carouselLoaders.push(
          new TemplateLazyLoad(".lazy-template-image", clone[0]),
        );
        clone.removeClass("image-row-template");
        if (this.carouselLoaders.length === 1) {
          clone.removeClass("hide");
        }
        clone.addClass("image-row");
        carousel.append(clone);
      }
    }
    // Show controls for more than 1 image
    this.displayCarouselControls(this.carouselImages.length > 1);
    // The overlay renders template after template with this one renderer: an inline
    // display beats any class rule, so the container's visibility is set here, both ways
    this.carouselContainer.toggle(this.carouselLoaders.length > 0);
    // No banner image loaded: the consumers make room for the title at the top
    this.root.toggleClass(
      "is-without-banner",
      this.carouselLoaders.length === 0,
    );
  }

  private displayCarouselControls(state: boolean): void {
    const carouselControls = this.carouselContainer.find(".controls");
    const controlButtons = carouselControls.find(".control-button");
    controlButtons.off("click.template");

    if (state) {
      controlButtons.on("click.template", (e) => {
        this.switchCarouselImage(+$(e.currentTarget).attr("data-direction"));
      });
      carouselControls.show();
    } else {
      carouselControls.hide();
    }
  }

  private switchCarouselImage(direction: number): void {
    const carousel = this.carouselContainer.find(".carousel");

    if (this.carouselImages.length === 0) {
      return;
    }
    this.carouselIndex += direction;

    if (this.carouselIndex < 0) {
      this.carouselIndex = this.carouselImages.length - 1;
    }
    if (this.carouselIndex >= this.carouselImages.length) {
      this.carouselIndex = 0;
    }

    carousel.children().addClass("hide");
    carousel.find(".image-row").eq(this.carouselIndex).removeClass("hide");
  }
}
